import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type Theme = "light" | "dark" | "system";
type Ctx = { theme: Theme; setTheme: (t: Theme) => void; resolved: "light" | "dark" };

const ThemeContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "saos-theme";

function applyTheme(theme: Theme): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  const resolved =
    theme === "system"
      ? window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light"
      : theme;
  document.documentElement.classList.toggle("dark", resolved === "dark");
  return resolved;
}

function defaultThemeForPath(pathname: string): Theme {
  // Authenticated app defaults to dark. Marketing, demo, auth, and other
  // public pages default to light. Users can still toggle either way.
  if (pathname.startsWith("/_authenticated") || pathname.startsWith("/admin") ||
      pathname.startsWith("/workspaces") || pathname.startsWith("/settings") ||
      pathname.startsWith("/dashboard") || pathname.startsWith("/command-center") ||
      pathname.startsWith("/people") || pathname.startsWith("/anomalies") ||
      pathname.startsWith("/digests") || pathname.startsWith("/training") ||
      pathname.startsWith("/calendar") || pathname.startsWith("/bonus-builder") ||
      pathname.startsWith("/swipe") || pathname.startsWith("/links") ||
      pathname.startsWith("/lenses") || pathname.startsWith("/contacts") ||
      pathname.startsWith("/imports") || pathname.startsWith("/onboarding") ||
      pathname.startsWith("/intelligence")) {
    return "dark";
  }
  return "light";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("light");
  const [resolved, setResolved] = useState<"light" | "dark">("light");

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    const initial = saved ?? defaultThemeForPath(window.location.pathname);
    setThemeState(initial);
    setResolved(applyTheme(initial));
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      if ((localStorage.getItem(STORAGE_KEY) as Theme | null) === "system") {
        setResolved(applyTheme("system"));
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setTheme = (t: Theme) => {
    localStorage.setItem(STORAGE_KEY, t);
    setThemeState(t);
    setResolved(applyTheme(t));
  };

  return <ThemeContext.Provider value={{ theme, setTheme, resolved }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
